<template>
	<div>
		<div class="container-error-list">
			<!-- Search Filter -->
			<BaseSearchFilter show>
				<template #body>
					<CCol md="3">
						<BaseDropDown
							v-model="filter.type"
							:options="ERROR_TYPE_OPTIONS"
							:label-drop-down="$t('label.errorStatus')"
							:placeholder="$t('message.allIssueTypes')"
							:searchable="false"
							label="name"
							track-by="value"
							@input="handleTypeChange"
						/>
					</CCol>
					<CCol md="3">
						<BaseDropDown
							v-model="filter.channels"
							:options="channelOptions"
							:label-drop-down="$t('label.channel')"
							:placeholder="$t('message.selection')"
							:allow-empty="false"
							searchable
							label="name"
							track-by="value"
							@input="handleChannelChange"
						/>
					</CCol>
				</template>
				<template #footer>
					<CCol md="8">
						{{ $t('message.issueFound', { number: totalCount }) }}
					</CCol>
					<CCol md="4">
						<button @click="onResetClick">
							{{ $t('button.resetFilters') }}
						</button>
					</CCol>
				</template>
			</BaseSearchFilter>

			<!-- Table, Pagination -->
			<BaseTable
				:is-loading="isLoading"
				:pagination="pagination"
				:fields="OPENNED_ERROR_TABLE_FIELDS"
				:items="dataTable"
				hover
				fixed
				class="table-custom"
				@onPaginationClick="handlePageClick"
			>
				<template #actionOpennedError="{ item }">
					<CButton
						block
						class="btn-tertiary"
						@click="handleOpenModalRemove(item)"
					>
						{{ $t('button.resolve') }}
					</CButton>
				</template>
				<template #no-items-view>
					<div class="empty-table-element">
						<p class="subtitle">
							{{ $t('message.notFoundErrorList') }}
						</p>
					</div>
				</template>
			</BaseTable>

			<!-- Modal Confirm Delete -->
			<ModalResolvedIssue
				ref="modal-resolved-issue"
				:title="$t('message.resolveErrorIssue')"
				:item="modalIssueItem"
				:status="query.status"
				:is-loading="isLoading"
				@onCancel="handleCancelRemove"
				@onSubmit="handleSubmit"
			/>
			<!-- End Modal Confirm Delete -->
		</div>
	</div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import {
	OPENNED_ERROR_TABLE_FIELDS,
	ERROR_TYPE_OPTIONS,
	ERROR_TITLE,
} from '../enums/error';
import { randomSearchString, numberFormat } from '../helpers/utils';
import { getSelectedValue } from '../helpers/dropdown';
import { updateUrl } from '../helpers/mixins';

export default {
	name: 'ErrorOpenedIssue',
	mixins: [updateUrl],
	data() {
		return {
			OPENNED_ERROR_TABLE_FIELDS,
			ERROR_TYPE_OPTIONS,
			query: {
				status: 'opening',
				type: this.$route.query.type || null,
				channels: this.$route.query.channels || null,
				page: this.$route.query.page || null,
				r: randomSearchString(),
			},
			filter: {
				type: getSelectedValue(this.$route.query.type, ERROR_TYPE_OPTIONS),
				channels: getSelectedValue(null, this.channelOptions),
			},
			modalSelected: null,
		};
	},
	computed: {
		...mapState('error', {
			data: 'data',
			isLoading: 'isLoading',
			pagination: 'pagination',
		}),
		...mapGetters('channelList', {
			channelOptions: 'channelOptions',
		}),
		isActive() {
			return (
				this.filter.type?.value !== null ||
				this.filter.channels?.value !== null
			);
		},
		dataTable() {
			return this.data.map((item) => ({
				id: item.id,
				type: ERROR_TITLE.ERROR[item.type],
				message: item.message,
				channels: item.channels,
				reportedAt: item.reportedAt,
				actionOpennedError: item.id,
			}));
		},
		totalCount() {
			return numberFormat(this.pagination.total);
		},
		modalIssueItem() {
			return this.data.find(((obj) => obj.id === this.modalSelected));
		},
	},
	async created() {
		await this.fetchData();
		const defaultChannel = this.channelOptions.length ? this.channelOptions[0].value : null;
		const channel = this.$route.query.channels ? Number(this.$route.query.channels, 10) : defaultChannel;
		this.filter.channels = getSelectedValue(channel, this.channelOptions);
		if (defaultChannel) {
			this.query.channels = defaultChannel;
			this.query.r = null;
			this.updateUrl(this.query);
		}
	},
	methods: {
		...mapActions('error', {
			getErrorData: 'getErrorData',
			updateErrorResolve: 'updateErrorResolve',
		}),
		...mapActions('channelList', {
			getChannels: 'getChannels',
		}),
		fetchData() {
			return Promise.all([
				this.getErrorData(this.query),
				this.getChannels(),
			]);
		},
		onResetClick() {
			this.query.type = null;
			this.query.channels = null;
			this.query.page = null;
			this.query.r = null;
			this.updateUrl(this.query);
		},
		handleChannelChange(option) {
			this.query.channels = option?.value;
			this.query.page = null;
			this.updateUrl(this.query);
		},
		handleTypeChange(option) {
			this.query.type = option?.value;
			this.query.page = null;
			this.updateUrl(this.query);
		},
		handleOpenModalRemove(id) {
			this.$refs['modal-resolved-issue'].open();
			this.modalSelected = id;
		},
		handleCancelRemove() {
			this.$refs['modal-resolved-issue'].close();
		},
		handlePageClick(pageNum) {
			this.query.page = pageNum;
			this.updateUrl(this.query);
		},
		async handleSubmit(data) {
			await this.updateErrorResolve({
				id: data.id,
				params: {
					status: 'resolved',
					remark: data.remark,
				},
			});
			await this.handleCancelRemove();
			await this.fetchData();
		},
	},
};
</script>

<style lang="scss" scoped>
::v-deep .container-filter {
	&-body {
		background-color: transparent;
		margin: 0 rem(-12);
	}

	&-header {
		display: none;
	}
}

::v-deep .table-custom {
	.issue-id-col {
		min-width: rem(120);
	}
}
</style>
